import React, { useMemo } from 'react';
import { Grid, Typography, Skeleton, useTheme } from '@mui/material';

export type EnhancedSelectItemSkeletonType =
  | 'singleSelection'
  | 'multipleSelection';

type Props = {
  type?: EnhancedSelectItemSkeletonType;
  index: number;
  isDense?: boolean;
};

const ListItemSkeleton = ({
  type = 'multipleSelection',
  index,
  isDense = false,
}: Props) => {
  const isSingle = type === 'singleSelection';
  const theme = useTheme();

  const maxWidthPercentage = 105;
  const minWidthPercentage = isSingle ? 50 : 30;

  const LCG = (a: number) => {
    const A = Math.imul(48271, a * index * 100) | 0 % 2147483647;

    return Math.max(
      Math.floor(((A & 2147483647) / 214748) % maxWidthPercentage),
      minWidthPercentage,
    );
  };

  const randomSkeletonLength = LCG(index);

  const skeletonIcon = useMemo(() => {
    switch (type) {
      case 'multipleSelection':
        return (
          <Skeleton
            animation="wave"
            variant="rectangular"
            sx={{
              width: 24,
              height: 24,
              padding: 9,
              borderRadius: theme.spacing(0.5),
            }}
          />
        );

      case 'singleSelection':
        return (
          <Skeleton
            animation="wave"
            variant="circular"
            sx={{
              width: 24,
              height: 24,
              padding: 9,
            }}
          />
        );
    }
  }, [type]);

  return (
    <Grid
      container
      justifyContent="flex-start"
      alignItems="center"
      wrap="nowrap"
      sx={{
        height: theme.spacing(isDense ? 5.25 : 6.75),
        padding: theme.spacing(1, 2, 1, isSingle ? 0 : 2),
      }}
    >
      <Grid
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 52,
          height: 42,
        }}
      >
        {skeletonIcon}
      </Grid>
      <Typography variant="h3" style={{ width: '100%', marginTop: -1 }}>
        <Skeleton
          animation="wave"
          variant="text"
          width={`${randomSkeletonLength}%`}
        />
      </Typography>
    </Grid>
  );
};

export default ListItemSkeleton;
